/**
 * Eric / Vincent (2026-05-14, refacto Option A Phase 3) — Pure helpers
 * extracted from VideoDetailPage.js to keep the page focused on rendering
 * and state orchestration. These functions have NO React state and NO
 * external dependencies beyond browser APIs — they can be unit-tested.
 */
import { toast } from "sonner";

/**
 * Convert seconds to a HH:MM:SS or MM:SS string.
 * Used to render video timestamps next to procedure/Q&R cards.
 */
export function formatTimecode(seconds) {
  if (typeof seconds !== "number" || !isFinite(seconds) || seconds < 0) return "00:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/**
 * Open a new window with the markdown report rendered as HTML and
 * trigger the browser's print dialog (user picks "Save as PDF" themselves).
 * @param {{title: string, htmlContent: string}} modal - report data
 */
export function downloadReportPDF(modal) {
  if (!modal) return;
  const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>${modal.title}</title>
    <style>
      body{font-family:'Helvetica Neue',Arial,sans-serif;max-width:780px;margin:30px auto;padding:0 20px;color:#0f172a;line-height:1.55}
      h1{font-size:24px;border-bottom:2px solid #002FA7;padding-bottom:6px}
      h2{font-size:18px;margin-top:24px;color:#002FA7}
      h3{font-size:15px;margin-top:18px}
      em,i{color:#52525b}
      strong{color:#0f172a}
      pre,code{background:#f4f4f5;padding:2px 6px;border-radius:3px;font-family:Menlo,monospace;font-size:12px}
      ul{padding-left:20px}
      @media print { @page { margin: 1.6cm } }
    </style></head><body>${modal.htmlContent || ""}</body></html>`;
  const w = window.open("", "_blank");
  if (!w) {
    toast.error("Impossible d'ouvrir la fenêtre d'impression. Vérifiez que les pop-ups sont autorisés.");
    return;
  }
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try { w.focus(); w.print(); } catch (e) { /* user cancelled */ }
  }, 400);
}

/**
 * Trigger a client-side download of a .md file.
 * @param {{title: string, markdown: string}} modal
 */
export function downloadReportMarkdown(modal) {
  if (!modal) return;
  const blob = new Blob([modal.markdown], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${modal.title.replace(/[^a-z0-9-]+/gi, "_").slice(0, 80)}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
